import { useEffect, useState } from 'react';

import { Box, ButtonBase, Slide, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DoneIcon from '@material-ui/icons/Done';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import clsx from 'clsx';
import copy from 'copy-to-clipboard';
import { useSnackbar } from 'notistack';

import MessageBase, { MessageBaseProps } from 'components/MessageBase';
import { useSearchQuery } from 'contexts/SearchQueryContext';
import { ReplyMensagemInterativaInterface } from 'store/reducers/mensagens';
import { getMentionColors } from 'utils/mentionColors';
import { renderTextWithMentions } from 'utils/renderTextWithMentions';

import MessageErrorFallback from './MessageErrorFallback';

type TextMessageProps = MessageBaseProps;

const THREE_SECONDS_IN_MILLISECONDS = 3000; // 3s

const useStyles = makeStyles(
  ({ breakpoints, palette, shadows, zIndex, typography }) => ({
    root: {
      maxWidth: '65%',
      [breakpoints.down('sm')]: {
        maxWidth: '100%',
      },
    },
    text: {
      wordBreak: 'break-word',
      '& > em': {
        fontSize: typography.pxToRem(24),
        fontStyle: 'normal',
      },
      '& .search-highlight': {
        backgroundColor: '#ffeb3b',
        color: '#000',
        padding: '0 2px',
        borderRadius: '2px',
      },
      '& a.mention': {
        borderRadius: 4,
        ...getMentionColors(palette),
        paddingInline: 4,
        fontWeight: 500,
        textDecoration: 'none',
        cursor: 'pointer',
        pointerEvents: 'auto',
      },
    },
    copyButton: {
      borderRadius: '50%',
      boxShadow: shadows[6],
      zIndex: zIndex.drawer + 1,
      backgroundColor: palette.primary.main,
    },
    copyIcon: {
      paddingRight: 3,
      paddingLeft: 3,
      color: palette.getContrastText(palette.primary.main),
    },
  }),
);

export default function TextMessage(props: TextMessageProps) {
  const classes = useStyles(props);
  const { enqueueSnackbar } = useSnackbar();
  const searchQuery = useSearchQuery();

  const [copied, setCopied] = useState(false);
  const [showCopyButton, setShowCopyButton] = useState(false);

  useEffect(() => {
    if (copied) {
      const timeoutId = setTimeout(() => {
        setCopied(false);
      }, THREE_SECONDS_IN_MILLISECONDS);

      return () => clearTimeout(timeoutId);
    }
    return () => null;
  }, [copied]);

  try {
    const {
      mensagem: { mensagem, whatsappInteractiveComponents },
    } = props;

    const interactiveComponents =
      whatsappInteractiveComponents as ReplyMensagemInterativaInterface;

    const texto =
      interactiveComponents?.list_reply?.title ||
      interactiveComponents?.button_reply?.title ||
      mensagem;

    const html = renderTextWithMentions(
      texto,
      props.mensagem.contatosMencionados ?? [],
      searchQuery,
    );

    const CopyIcon = copied ? DoneIcon : FileCopyIcon;

    const handleMouseEnter = () => setShowCopyButton(true);
    const handleMouseLeave = () => setShowCopyButton(false);

    const handleCopy = () => {
      setCopied(true);
      copy(texto);
      enqueueSnackbar('Texto copiado para a àrea de transferência.');
    };

    return (
      <MessageBase maxWidth="65%" {...props}>
        <Box
          position="relative"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Typography
            color="inherit"
            component="div"
            variant="body2"
            className={classes.text}
            dangerouslySetInnerHTML={{ __html: html }}
          />

          <Slide
            direction="left"
            in={showCopyButton}
            mountOnEnter
            unmountOnExit
          >
            <Box position="absolute" top={0} bottom={0} right={0}>
              <ButtonBase
                title="Copiar texto"
                aria-label="copy text"
                onClick={handleCopy}
                className={clsx(classes.copyButton)}
              >
                <CopyIcon fontSize="small" className={classes.copyIcon} />
              </ButtonBase>
            </Box>
          </Slide>
        </Box>
      </MessageBase>
    );
  } catch (error) {
    return <MessageErrorFallback {...props} />;
  }
}
